import React from "react";
import { ShieldCheck, Scale, AlertTriangle, Copyright } from "lucide-react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

// -----------------------------------------------------------------------------
// Legal — the public IP / Trademark / Patent notice page. Similar to the
// Bandhel Cease-&-Desist template but tailored for find2near itself. Linked
// from the footer + returned when copy-protection blocks an action.
// -----------------------------------------------------------------------------
export default function Legal() {
  useSeo({
    title: "Legal Notice · © Trademark · Patent Pending · find2near",
    description: "find2near copyright, trademark and patent pending notice. Unauthorised copying, framing, cloning or reverse-engineering of this platform is a legal violation.",
    canonical: "/legal",
    robots: "index,follow",
  });

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8" data-testid="legal-page">
      <div className="rounded-3xl bg-white border border-red-200 p-6 md:p-8 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-red-50 border border-red-200 grid place-items-center text-red-700"><Scale size={20} /></div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-black text-red-700">Legal Notice · Reference no. FN-LEGAL-001</div>
            <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Copyright · Trademark · Patent Pending</h1>
          </div>
        </div>
        <p className="text-sm text-stone-700 leading-relaxed">
          The <b>find2near</b> platform, brand, source-code, user-experience design, database schema, algorithms, badges,
          feature naming (including but not limited to <i>Hand-2-Pay</i>, <i>Smart Match</i>, <i>WUID</i>, <i>NVN</i>, <i>Priority Card</i>,
          <i>Product Tag QR Generator</i>, <i>MetalPocket</i>, <i>Foundation Transparency Report</i>, <i>Live Complaint</i>, and every
          module in the Government Portal, Officials Dashboard, Volunteer & Civic Builder, Public Notice Board and
          Universal School Portal) are the exclusive intellectual property of the find2near team.
        </p>

        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-widest font-black text-red-800 flex items-center gap-2"><AlertTriangle size={14} /> Prohibited without written permission</div>
          <ul className="list-disc pl-5 text-sm text-stone-800 space-y-1">
            <li>Cloning, forking, framing or embedding this UI or its screens.</li>
            <li>Republishing our copy, badges, category taxonomy or QR payload spec.</li>
            <li>Reverse-engineering, decompiling or scraping our API endpoints, database or feed layout.</li>
            <li>Using the <b>“find2near”</b> word-mark or a confusingly similar mark in any storefront, product or service.</li>
            <li>Copying the Hand-2-Pay palm/QR overlay flow, Smart Match scorer, WUID collision-check or NVN registry design.</li>
          </ul>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div className="rounded-2xl bg-stone-50 border border-stone-200 p-4">
            <div className="flex items-center gap-2 text-stone-800 font-black mb-1"><Copyright size={16} /> Copyright</div>
            <p className="text-xs text-stone-600 leading-relaxed">© 2026 find2near. All rights reserved. The platform source code and UI are protected under the Copyright Act, 1957 and international treaties.</p>
          </div>
          <div className="rounded-2xl bg-stone-50 border border-stone-200 p-4">
            <div className="flex items-center gap-2 text-stone-800 font-black mb-1"><ShieldCheck size={16} /> Trademark & Patent Pending</div>
            <p className="text-xs text-stone-600 leading-relaxed">The find2near word-mark, logo and feature-naming are trademarks / trademark applications. Several backend flows are Patent Pending under the Patents Act, 1970.</p>
          </div>
        </div>

        <p className="text-sm text-stone-700 leading-relaxed">
          <b>Enforcement.</b> Unauthorised use is a cognizable, non-bailable offence under Sections 103 & 104 of the Trade Marks Act, 1999
          (jail term 6 months – 3 years + fine). Patent violation invites injunction, seizure of infringing systems under the Patents Act,
          1970 and compensation via <i>Account of Profits</i>. We will pursue civil damages, criminal complaints and account-of-profits
          claims to the fullest extent of the law.
        </p>

        <p className="text-sm text-stone-700 leading-relaxed">
          <b>Reporting infringement / requesting a licence.</b> Email <a href="mailto:legal@find2near.com" className="text-red-700 font-bold underline">legal@find2near.com</a>
          with the URL, screenshot and your contact details. Genuine licensing requests are welcome and we typically respond within 5 business days.
        </p>

        <div className="text-[10px] text-stone-500 border-t border-stone-200 pt-3">
          This page also serves as the copy that will be delivered on the clipboard whenever content is copied from the find2near site — as a persistent watermark.
        </div>

        <div className="pt-2">
          <Link to="/" className="text-sm font-bold text-red-700 hover:underline">← Back to find2near</Link>
        </div>
      </div>
    </div>
  );
}
